import { Input } from "../ui/input";
import { Label } from "../ui/label";

// Blank reason/approval/notified fields shared by the schedule-change and
// alternate-plan forms.
export const EMPTY_REASON = {
  reason_category: "",
  reason_notes: "",
  approved_by_id: "",
  approved_by_name: "",
  parent_notified: false,
  parent_notified_date: "",
};

// Reason categories — keep in sync with backend schedule_changes.REASON_CATEGORIES.
export const REASON_CATEGORIES = [
  "IEP/team decision",
  "behavior plan",
  "medical/family",
  "transition plan",
  "district/LEA request",
  "student/family request",
  "other",
];

/**
 * Shared "why / who / notified" fields for recording a schedule change or an
 * alternate plan. Writes reason_category, reason_notes, approved_by_id,
 * approved_by_name, parent_notified, parent_notified_date onto `form`.
 */
export default function ScheduleReasonFields({ form, setForm, staff = [], testidPrefix = "" }) {
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  return (
    <div className="space-y-4 rounded-lg border bg-slate-50/60 p-3">
      <div className="space-y-2">
        <Label>Reason <span className="text-red-500">*</span></Label>
        <select
          value={form.reason_category || ""}
          onChange={(e) => set({ reason_category: e.target.value })}
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
          data-testid={`${testidPrefix}reason-category`}
        >
          <option value="">Select a reason…</option>
          {REASON_CATEGORIES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label>Reason notes</Label>
        <Input
          value={form.reason_notes || ""}
          onChange={(e) => set({ reason_notes: e.target.value })}
          placeholder="Optional detail (e.g. per 10/14 team meeting)"
          data-testid={`${testidPrefix}reason-notes`}
        />
      </div>

      <div className="space-y-2">
        <Label>Approved by</Label>
        <select
          value={form.approved_by_id || ""}
          onChange={(e) => {
            const s = staff.find((x) => x.id === e.target.value);
            set({ approved_by_id: e.target.value, approved_by_name: s ? s.name : "" });
          }}
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
          data-testid={`${testidPrefix}approved-by`}
        >
          <option value="">Not specified</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.parent_notified}
            onChange={(e) => set({
              parent_notified: e.target.checked,
              parent_notified_date: e.target.checked ? (form.parent_notified_date || todayStr()) : "",
            })}
            className="h-4 w-4 accent-[#00305E]"
            data-testid={`${testidPrefix}parent-notified`}
          />
          <span className="text-sm">Parent/guardian notified</span>
        </label>
        {form.parent_notified && (
          <Input
            type="date"
            value={form.parent_notified_date || todayStr()}
            onChange={(e) => set({ parent_notified_date: e.target.value })}
            className="w-48"
            data-testid={`${testidPrefix}parent-notified-date`}
          />
        )}
      </div>
    </div>
  );
}
